import { v4 as uuidv4 } from 'uuid';
import {
  ARROW_UP_BUTTON_TOOLTIP,
  RESET_CHAT_HISTORY_TOOLTIP,
  RESET_DISABLED_TOOLTIP,
  TEXT_AREA_IDS,
  TEXT_AREA_PLACEHOLDER,
  UPLOAD_FILE_INPUT_ID,
} from 'components/AiMode/constants';
import UploadView from '../UploadView';
import styles from './styles.module.scss';
import ExplainerCard from '../ExplainerCard';
import { useAppContext } from 'context/AppContext';
import { IconID } from 'packages/radiant-react/icon';
import { useUploadFiles } from 'hooks/useUploadFiles';
import { Button } from 'packages/radiant-react/button';
import { TextArea } from 'packages/radiant-react/text-area';
import { useIsFirstTimeView } from 'hooks/useIsFirstTimeView';
import { Horizontal, Vertical } from 'packages/radiant-react/layout';
import { FirstTimeViewName, ImageType, MessageRoleType, MessageType, PdfFileType } from 'types';

function ChatInput({
  handleTextareaInput,
  handleEnterKeyPress,
  handleArrowUpClick,
  userInput,
}: {
  handleTextareaInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleEnterKeyPress: (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    userPdfAndImages?: { pdfs: PdfFileType[]; images: ImageType[] } | undefined
  ) => void;
  handleArrowUpClick: (
    userPdfAndImages?: { pdfs: PdfFileType[]; images: ImageType[] } | undefined
  ) => void;
  userInput: string;
}) {
  const { isStreaming, updateChatMessages, resetChatMessages } = useAppContext();
  const { handleDelete, handleUpload, userPdfAndImages, resetUserPdfAndImages } = useUploadFiles();
  const { showView, isSeenView, handleViewHover, handleViewClose } = useIsFirstTimeView(
    FirstTimeViewName.HasSeenFileUploadExplainer
  );

  const handleIsUpdateChatMessages = () => {
    if (userInput.trim() && !isStreaming) {
      const userMessage: MessageType = {
        role: MessageRoleType.USER,
        content: userInput.trim(),
        images: userPdfAndImages?.images,
        pdfFiles: userPdfAndImages?.pdfs,
        id: `${MessageRoleType.USER}-${uuidv4()}`,
      };
      const aiMessage: MessageType = {
        role: MessageRoleType.AI,
        content: '',
        id: `${MessageRoleType.AI}-${uuidv4()}`,
      };
      updateChatMessages([userMessage, aiMessage]);
      resetUserPdfAndImages();
    }
  };

  const isButtonDisabled = isStreaming ? false : !userInput.trim();

  return (
    <Vertical className={styles['container']}>
      <Vertical className={styles['chat-input-form']}>
        <TextArea
          id={TEXT_AREA_IDS.CHAT_INPUT}
          value={userInput}
          onChange={handleTextareaInput}
          placeholder={TEXT_AREA_PLACEHOLDER}
          autoFocus
          className={styles['chat-input-textarea']}
          onKeyDown={(e) => {
            handleEnterKeyPress(e, userPdfAndImages);
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleIsUpdateChatMessages();
            }
          }}
        />
        <Horizontal className={styles['buttons']}>
          <Horizontal className={styles['icon-upload']}>
            <label htmlFor={UPLOAD_FILE_INPUT_ID} onMouseEnter={handleViewHover}>
              <Button icon={IconID.PLUS} type="tertiary" className={styles['icon-upload-button']} />
            </label>
            {showView && !isSeenView && <ExplainerCard handleClose={() => handleViewClose(true)} />}
            <UploadView
              userPdfAndImages={userPdfAndImages}
              handleDelete={handleDelete}
              handleUpload={handleUpload}
            />
          </Horizontal>
          <Horizontal className={styles['send-and-reset-buttons']}>
            <Button
              icon={IconID.RESET}
              type="secondary"
              onClick={resetChatMessages}
              tooltip={isStreaming ? RESET_DISABLED_TOOLTIP : RESET_CHAT_HISTORY_TOOLTIP}
              isDisabled={isStreaming}
            />
            <Button
              type="primary"
              onClick={() => {
                handleArrowUpClick(userPdfAndImages);
                handleIsUpdateChatMessages();
              }}
              icon={isStreaming ? IconID.STOP_SQUARE : IconID.ARROW_UP}
              isDisabled={isButtonDisabled}
              tooltip={!isStreaming ? ARROW_UP_BUTTON_TOOLTIP : undefined}
            />
          </Horizontal>
        </Horizontal>
      </Vertical>
    </Vertical>
  );
}

export default ChatInput;
